import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import useClickOutside from 'app/hooks/useClickOutside';
import ChallengesList from 'app/pages/Gamification/components/ChallengesList';
import GamificationMenuLevelHeader from 'app/components/GamificationMenuLevelHeader';
import ChallengeModal from 'app/pages/Gamification/components/ChallengeModal';
import InviteModal from 'app/pages/Gamification/components/InviteModal';
import { useGamificationChallengesQuery } from 'app/pages/Gamification/hooks/useGamificationQuery';
import {
  isChallengeBlockedForAction,
  resolveChallengeStartDestination,
  shouldOpenInviteModal,
} from 'app/helpers/gamification/challengeActions';
import {
  isOnboardingChallenge,
  selectOnboardingChecklist,
} from 'app/helpers/gamification/challengeDomain';
import { isJuspaySpecialEvent } from 'app/helpers/gamification/challengePresentation';
import type { PlanExplorationOrigin } from 'app/hooks/usePlanExploration/types';
import { ROUTES_PATH } from 'app/constants/Routes';
import { isCommunityLink, openCommunity } from 'app/services/community';
import MgmSidebarBanner from 'app/components/Mgm/SidebarBanner';
import { trackMenuGamificationDetailsClicked } from 'app/pages/Gamification/helpers/trackGamificationEvents';
import OnboardingChecklist from './OnboardingChecklist';
import * as S from './styles';

type SidebarGamificationDropdownProps = {
  icon: React.ReactNode;
  ariaLabel: string;
  tooltip?: string;
  routeActive?: boolean;
  onNavigate: (path: string) => void;
  onOpenPlanExploration: (origin: PlanExplorationOrigin) => void;
  onOpen?: () => void;
};

type SidebarChallenge = {
  id: string | number;
  name: string;
  description?: string;
  points?: number;
  event?: string;
  user_status?: string;
  challenge_class?: string;
  display_order?: number;
};

const isSidebarChallenge = (challenge: unknown): challenge is SidebarChallenge => {
  if (!challenge || typeof challenge !== 'object') return false;
  const candidate = challenge as Record<string, unknown>;

  const hasValidId =
    typeof candidate.id === 'string' || typeof candidate.id === 'number';
  const hasValidName = typeof candidate.name === 'string';

  return hasValidId && hasValidName;
};

const SidebarGamificationDropdown: React.FC<
  SidebarGamificationDropdownProps
> = ({
  icon,
  ariaLabel,
  tooltip,
  routeActive = false,
  onNavigate,
  onOpenPlanExploration,
  onOpen,
}) => {
  const [open, setOpen] = useState(false);
  const [isChallengeModalOpen, setIsChallengeModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedChallenge, setSelectedChallenge] =
    useState<SidebarChallenge | null>(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, bottom: 0 });
  const hasOpenModal = isChallengeModalOpen || isInviteModalOpen;
  const {
    data: rawChallenges = [],
    isLoading: isLoadingChallenges,
    isFetching: isFetchingChallenges,
  } = useGamificationChallengesQuery({
    // Dropdown must not reuse a persisted empty/stale list from earlier failures
    // (the gamification page fetches imperatively and bypasses this cache).
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const challenges = useMemo(
    () => (rawChallenges as unknown[]).filter(isSidebarChallenge),
    [rawChallenges],
  );

  const onboardingChecklist = useMemo(
    () => selectOnboardingChecklist(challenges),
    [challenges],
  );

  const seasonChallenges = useMemo(
    () => challenges.filter(c => !isOnboardingChallenge(c)),
    [challenges],
  );

  const sidebarChallenges = useMemo(() => {
    const juspay = seasonChallenges.filter(c => isJuspaySpecialEvent(c.event));
    // compact ChallengesList already sorts and limits to 3 — slicing here would
    // drop items that sorting could have promoted into the top 3.
    return juspay.length > 0 ? juspay : seasonChallenges;
  }, [seasonChallenges]);

  const forceCloseDropdown = useCallback(() => setOpen(false), []);
  const handleClose = useCallback(() => {
    if (hasOpenModal) {
      return;
    }
    forceCloseDropdown();
  }, [hasOpenModal, forceCloseDropdown]);
  const containerRef = useClickOutside(handleClose, [
    'sidebar-gamification-menu-portal',
  ]);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setOpen((prev) => {
      const nextOpen = !prev;
      if (nextOpen) {
        onOpen?.();
      }
      return nextOpen;
    });
  };

  const handleViewAllChallenges = () => {
    trackMenuGamificationDetailsClicked();
    onNavigate(ROUTES_PATH.GAMIFICATION.BASE);
    forceCloseDropdown();
  };

  const handleCloseModals = () => {
    setSelectedChallenge(null);
    setIsChallengeModalOpen(false);
    setIsInviteModalOpen(false);
  };

  const handleChallengeClick = (challenge: SidebarChallenge) => {
    if (isChallengeBlockedForAction(challenge)) {
      return;
    }

    setSelectedChallenge(challenge);

    if (shouldOpenInviteModal(challenge)) {
      setIsInviteModalOpen(true);
      return;
    }

    setIsChallengeModalOpen(true);
  };

  const handleStartChallenge = () => {
    const destination = resolveChallengeStartDestination(selectedChallenge?.event);

    handleCloseModals();

    if (destination.type === 'external') {
      forceCloseDropdown();
      if (isCommunityLink(destination.url)) {
        openCommunity();
        return;
      }
      const newWin = window.open(destination.url, '_blank', 'noopener,noreferrer');
      if (newWin) newWin.opener = null;
      return;
    }

    if (destination.type === 'internal') {
      forceCloseDropdown();
      onNavigate(destination.path);
      return;
    }

    if (destination.type === 'modal') {
      forceCloseDropdown();
      onOpenPlanExploration(destination.origin);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen((prev) => !prev);
    }
  };

  const handlePanelKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  };

  useLayoutEffect(() => {
    if (!open || !containerRef.current) {
      return;
    }

    const sidebarColumn = containerRef.current.closest('[data-sidebar-column]');
    const leftPx = sidebarColumn
      ? sidebarColumn.getBoundingClientRect().right
      : containerRef.current.getBoundingClientRect().right;

    setMenuPosition({
      left: leftPx,
      bottom: 24,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleResize = () => handleClose();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open, handleClose]);

  const menuPanel = open ? (
    <S.MenuPanel
      role="dialog"
      aria-modal="false"
      aria-label="Gamificação"
      $left={menuPosition.left}
      $bottom={menuPosition.bottom}
      onKeyDown={handlePanelKeyDown}
    >
      <S.Content>
        <GamificationMenuLevelHeader />
        <MgmSidebarBanner />
        <S.ChallengesSection>
          <S.SectionTitle>
            {onboardingChecklist.isOnboardingMode
              ? 'Primeiros passos'
              : 'Turbine seus pontos'}
          </S.SectionTitle>
          {onboardingChecklist.isOnboardingMode ? (
            <OnboardingChecklist
              items={onboardingChecklist.items}
              completedCount={onboardingChecklist.completedCount}
              totalCount={onboardingChecklist.totalCount}
              loading={isLoadingChallenges}
              onItemSelect={handleChallengeClick}
            />
          ) : (
            <ChallengesList
              challenges={sidebarChallenges}
              loading={isFetchingChallenges}
              compact
              disableInternalFlow
              onChallengeSelect={handleChallengeClick}
              hideLeadingIcon
            />
          )}
          <S.Cta type="button" onClick={handleViewAllChallenges}>
            Ver gamificação completa
          </S.Cta>
        </S.ChallengesSection>
      </S.Content>
    </S.MenuPanel>
  ) : null;

  const tooltipAttributes = tooltip
    ? {
        'data-tooltip-id': 'sidebar-icon-tooltip',
        'data-tooltip-content': tooltip,
      }
    : {};

  return (
    <S.Wrapper ref={containerRef}>
      <S.IconWrapper
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...tooltipAttributes}
      >
        <S.Icon $active={open} $routeActive={routeActive}>
          {icon}
        </S.Icon>
      </S.IconWrapper>
      {open &&
        createPortal(
          <div id="sidebar-gamification-menu-portal">
            {menuPanel}
            <ChallengeModal
              isOpen={isChallengeModalOpen}
              challenge={
                selectedChallenge
                  ? {
                      name: selectedChallenge.name,
                      points: selectedChallenge.points,
                      description: selectedChallenge.description,
                      event: selectedChallenge.event,
                    }
                  : null
              }
              onClose={handleCloseModals}
              onStart={handleStartChallenge}
            />
            <InviteModal
              isOpen={isInviteModalOpen}
              challenge={
                selectedChallenge
                  ? {
                      name: selectedChallenge.name,
                      points: selectedChallenge.points,
                    }
                  : null
              }
              onClose={handleCloseModals}
            />
          </div>,
          document.body,
        )}
    </S.Wrapper>
  );
};

export default SidebarGamificationDropdown;
